import { CheckCircleOutlined, DeleteOutlined, DownloadOutlined, ExportOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Image, Input, Modal, Space, message } from "antd"
import React, { useEffect, useState } from "react"
import { t } from "../../../../languages/i18n"
import { MediaData } from "../../../../lib/config/types"
import { toImageSrc } from "@/lib/utils"
import { useDomainStore } from "@/stores/use-domain-store"
import { deleteMedia } from "../services/apiServices"
import { resolveMediaDomainParam } from "../utils/media-domain"
import FileTypeIcon, { isImageFile, isVideoFile } from "../components/FileTypeIcon"
import ImageEditor from "./ImageEditor"

interface MediaModalBodyProps {
  media: MediaData
  loading: boolean
  handleFormSubmit: (value: { title: string; alternativeText: string; caption: string }, file?: File) => Promise<void>
  refreshMediaList?: () => void
  setIsView: (value: boolean) => void
}

const MediaModalEditBody: React.FC<MediaModalBodyProps> = ({
  media,
  loading,
  handleFormSubmit,
  refreshMediaList,
  setIsView,
}) => {
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null)
  const [isEditingImage, setIsEditingImage] = useState(false)
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const domain = resolveMediaDomainParam(selectedDomain)
  const isImage = isImageFile(media)
  const isVideo = isVideoFile(media)
  const fileSrc = toImageSrc(media.url)

  useEffect(() => {
    const image = document.getElementById("Image") as HTMLFormElement
    image?.reset()
  }, [media, handleFormSubmit])

  useEffect(() => {
    if (editedImageUrl) {
      const imageElement = document.getElementById("Image") as HTMLImageElement
      if (imageElement) {
        imageElement.src = editedImageUrl
      }
    }
  }, [editedImageUrl])

  const handleDownloadFile = (e: React.MouseEvent) => {
    e.preventDefault()
    fetch(toImageSrc(media.url), {
      method: "GET",
      headers: {
        "Content-Type": media.mime,
      },
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", media.name)
        link.click()
      })
  }

  const handleDeletePermanently = (e: React.MouseEvent) => {
    e.preventDefault()
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this media file?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await deleteMedia(media.id, domain)
          message.success(t("Media file deleted successfully!"))
          refreshMediaList && refreshMediaList()
          setIsView(false)
        } catch (error) {
          message.error(t("Failed to delete media file."))
        }
      },
    })
  }

  return (
    <div style={{ height: "80vh", overflowY: "auto" }}>
      {isEditingImage ? (
        <ImageEditor
          media={media}
          isEditingImage={isEditingImage}
          setIsEditingImage={setIsEditingImage}
          handleFormSubmit={handleFormSubmit}
        />
      ) : (
        <div style={{ display: "flex", height: "100%", width: "100%" }}>
          <Space
            align="center"
            style={{
              display: "grid",
              justifyContent: "space-around",
              height: "100%",
              width: "65%",
            }}
            className="mt-5"
          >
            <div
              style={{
                display: "grid",
                justifyItems: "center",
                maxHeight: "100%",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  maxHeight: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {isImage ? (
                  <Image
                    src={fileSrc}
                    className="object-contain"
                    style={{ maxHeight: "65vh", maxWidth: "100%", width: "auto" }}
                  />
                ) : isVideo ? (
                  <video
                    src={fileSrc}
                    controls
                    className="object-contain"
                    style={{ maxHeight: "65vh", maxWidth: "100%", width: "auto" }}
                  />
                ) : (
                  // Không phải ảnh/video (pdf, doc, octet-stream…): hiện icon theo loại
                  // thay vì đổ vào <Image> làm vỡ ảnh.
                  <div className="flex flex-col items-center justify-center gap-3 text-gray-500">
                    <FileTypeIcon mime={media.mime} name={media.name} fontSize={96} />
                    <span className="max-w-[320px] break-all text-center text-sm">{media.name}</span>
                  </div>
                )}
              </div>

              {/* Chỉnh sửa ảnh chỉ khả dụng với file ảnh. */}
              {isImage && (
                <Button className="mt-4" onClick={() => setIsEditingImage(true)}>
                  {t("Edit Image")}
                </Button>
              )}
            </div>
          </Space>

          <Space className="w-[30%] h-[80%] ml-5 pr-0 block">
            <Flex className="flex-col bg-[#f6f7f7] p-[10px_10px_15px_20px] h-[100%]">
              <div className="text-xs border-b border-black pb-1.5 h-[100%]">
                <p>
                  <strong>{t("Uploaded on:")}</strong> {media.createdAt}
                </p>
                <p>
                  <strong>{t("File name:")}</strong> {media.name}
                </p>
                <p>
                  <strong>{t("File type:")}</strong> {media.mime}
                </p>
                <p>
                  <strong>{t("File size:")}</strong> {media.size} KB
                </p>
                {media.width && media.height ? (
                  <p>
                    <strong>{t("Dimensions:")}</strong> {media.width} x {media.height} pixels
                  </p>
                ) : null}
              </div>

              <Form
                id="form"
                className="mt-5 mb-6"
                size="large"
                labelAlign="left"
                labelCol={{ span: 10 }}
                name="editMedia"
                initialValues={{
                  alternativeText: media.alternativeText,
                  title: media.name,
                  caption: media.caption,
                }}
                onFinish={values => handleFormSubmit(values)}
              >
                <Form.Item label={t("Alternative text")} name="alternativeText">
                  <Input className="mb-4" placeholder={t("Enter alternative text")} />
                </Form.Item>

                <Form.Item label={t("Title")} name="title">
                  <Input className="mb-4" placeholder={t("Enter title")} />
                </Form.Item>

                <Form.Item label={t("Caption")} name="caption">
                  <Input className="mb-6" placeholder={t("Enter caption")} />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<CheckCircleOutlined />}>
                    {t("Save Changes")}
                  </Button>
                </Form.Item>
              </Form>
              <div className="text-blue-600 text-xs border-t border-black pt-2.5 h-[30vh]">
                <Button
                  className="mr-3 mt-3"
                  href={fileSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<ExportOutlined />}
                >
                  {t("Mở link")}
                </Button>
                <Button className="mr-3 mt-3" onClick={handleDownloadFile} icon={<DownloadOutlined />}>
                  {t("Download file")}
                </Button>
                <Button className="mt-3" danger onClick={handleDeletePermanently} icon={<DeleteOutlined />}>
                  {t("Delete permanently")}
                </Button>
              </div>
            </Flex>
          </Space>
        </div>
      )}
    </div>
  )
}

export default MediaModalEditBody
